import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import paymentService from '../services/paymentService';
import errorHandler from '../utils/errorHandler';

const PaymentContext = createContext();

export const usePayment = () => {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error('usePayment must be used within a PaymentProvider');
    }
    return context;
};

export const PaymentProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

    // Initialize payment service
    useEffect(() => {
        initializePaymentService();
    }, []);

    const initializePaymentService = async () => {
        try {
            setLoading(true);
            await paymentService.initializeProviders();
            const methods = paymentService.getAvailablePaymentMethods();
            setPaymentMethods(methods);
            
            // Set default payment method
            if (methods.length > 0) {
                setSelectedPaymentMethod(methods[0].id);
            }
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Payment initialization' });
        } finally {
            setLoading(false);
        }
    };

    const processPayment = async (paymentData) => {
        try {
            setLoading(true);

            const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
            if (!method) {
                throw new Error('No payment method selected');
            }

            let result;

            switch (selectedPaymentMethod) {
                case 'razorpay':
                    result = await processRazorpayPayment(paymentData);
                    break;
                case 'stripe':
                    result = await processStripePayment(paymentData);
                    break;
                case 'cod':
                    result = await processCODPayment(paymentData);
                    break;
                default:
                    throw new Error('Unsupported payment method');
            }

            message.success('Payment processed successfully!');
            return result;

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Payment processing' });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const processRazorpayPayment = async (paymentData) => {
        try {
            // Create Razorpay order
            const order = await paymentService.createRazorpayOrder(
                paymentData.amount,
                paymentData.currency,
                paymentData.booking_id
            );

            // Process payment
            const result = await paymentService.processRazorpayPayment({
                ...paymentData,
                razorpay_order_id: order.id
            });

            return result;
        } catch (error) {
            throw error;
        }
    };

    const processStripePayment = async (paymentData) => {
        try {
            // Create payment intent
            const paymentIntent = await paymentService.createPaymentIntent(
                paymentData.amount,
                paymentData.currency,
                {
                    booking_id: paymentData.booking_id,
                    customer_name: paymentData.customer_name
                }
            );

            // Process payment
            const result = await paymentService.processStripePayment({
                ...paymentData,
                stripe_payment_intent_client_secret: paymentIntent.client_secret
            });

            return result;
        } catch (error) {
            throw error;
        }
    };

    const processCODPayment = async (paymentData) => {
        try {
            // For COD, just confirm the booking
            const response = await fetch('/api/bookings/confirm-cod', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    booking_id: paymentData.booking_id,
                    amount: paymentData.amount,
                    customer_name: paymentData.customer_name,
                    customer_email: paymentData.customer_email,
                    customer_phone: paymentData.customer_phone
                })
            });

            const result = await response.json();

            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.message || 'COD confirmation failed');
            }
        } catch (error) {
            throw error;
        }
    };

    const getPaymentStatus = async (bookingId) => {
        try {
            const status = await paymentService.getPaymentStatus(bookingId);
            return status;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get payment status' });
            throw error;
        }
    };

    const refundPayment = async (paymentId, amount = null, reason = 'Customer request') => {
        try {
            setLoading(true);
            const result = await paymentService.refundPayment(paymentId, amount, reason);
            return result;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Refund payment' });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const loadPaymentHistory = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const history = await paymentService.getPaymentHistory(page, limit);
            setPaymentHistory(history);
            return history;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Load payment history' });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const setPaymentMethod = (methodId) => {
        const method = paymentMethods.find(m => m.id === methodId);
        if (method) {
            setSelectedPaymentMethod(methodId);
        }
    };

    const getSelectedPaymentMethod = () => {
        return paymentMethods.find(m => m.id === selectedPaymentMethod);
    };

    const isPaymentMethodAvailable = (methodId) => {
        return paymentMethods.some(m => m.id === methodId && m.isAvailable);
    };

    const getPaymentMethodIcon = (methodId) => {
        const method = paymentMethods.find(m => m.id === methodId);
        return method?.icon || '💳';
    };

    const getPaymentMethodDescription = (methodId) => {
        const method = paymentMethods.find(m => m.id === methodId);
        return method?.description || 'Payment method';
    };

    const value = {
        loading,
        paymentMethods,
        paymentHistory,
        selectedPaymentMethod,
        processPayment,
        getPaymentStatus,
        refundPayment,
        loadPaymentHistory,
        setPaymentMethod,
        getSelectedPaymentMethod,
        isPaymentMethodAvailable,
        getPaymentMethodIcon,
        getPaymentMethodDescription,
    };

    return (
        <PaymentContext.Provider value={value}>
            {children}
        </PaymentContext.Provider>
    );
};

export default PaymentProvider;
